import React from 'react'
import { useDispatch } from 'react-redux'
import { useTable } from 'hooks'
import { getColumns } from './model'
import { toUserDepartments } from 'ducks/router'
import { fetchProjects } from 'api/projects'
import {
  postProjectAccess,
  deleteProjectAccess,
  postDepartmentProjectsAccess,
  deleteDepartmentProjectsAccess,
  fetchAllProjectsAccess,
} from 'api/users'

const useUserProjectsTableMutations = () => {
  const [data, setData] = React.useState(false)

  const handleFetchProjects = React.useCallback(
    async (id, { limit, offset, search }) => {
      const { response } = await fetchProjects({
        limit,
        offset,
        search,
        department: id,
      })

      setData(response)
    },
    [fetchProjects, setData],
  )

  return {
    handleFetchProjects,
    setData,
    data,
  }
}

const useAllProjectsAccess = ({ updateTable, departmentId, user }) => {
  const [isAllProjectsAccess, setIsAllProjectsAccess] = React.useState(false)

  const handleFetchAllProjectsAccess = React.useCallback(
    async ({ user, departmentId }) => {
      const { response } = await fetchAllProjectsAccess({
        pk: user,
        departmentId,
      })

      if (response) {
        setIsAllProjectsAccess(response.has_access_to_all_projects)
      }
    },
    [fetchAllProjectsAccess, setIsAllProjectsAccess],
  )

  const handleEditDepartmentProjectsAccess = React.useCallback(
    async ({ selected, user, departmentId }) => {
      const apiMethod = selected
        ? deleteDepartmentProjectsAccess
        : postDepartmentProjectsAccess

      await apiMethod({
        pk: user,
        departmentId,
      })

      updateTable()
      setIsAllProjectsAccess(!selected)
    },
    [
      deleteDepartmentProjectsAccess,
      postDepartmentProjectsAccess,
      setIsAllProjectsAccess,
      updateTable,
    ],
  )

  React.useEffect(() => {
    handleFetchAllProjectsAccess({ user, departmentId })
  }, [departmentId, user])

  const toggleSelectAll = React.useCallback(() => {
    handleEditDepartmentProjectsAccess({
      selected: isAllProjectsAccess,
      user,
      departmentId,
    })
  }, [isAllProjectsAccess])

  return {
    isAllProjectsAccess,
    toggleSelectAll,
    handleFetchAllProjectsAccess,
  }
}

export const useUserProjectsTable = state => {
  const [signalTableUpdate, setSignalTableUpdate] = React.useState(false)

  const updateTable = () => {
    setSignalTableUpdate(!signalTableUpdate)
  }

  const { mapSortingPropToQueryParam, search, id, selectedUser, department } =
    state

  const user = selectedUser.id

  const columns = getColumns(department.name)

  const dispatch = useDispatch()

  const { handleFetchProjects, data, setData } = useUserProjectsTableMutations()

  const { isAllProjectsAccess, toggleSelectAll, handleFetchAllProjectsAccess } =
    useAllProjectsAccess({
      updateTable,
      departmentId: id,
      user,
    })

  const handleRefetch = React.useCallback(
    ({ limit, offset, search }) => {
      handleFetchProjects(id, { limit, offset, search })
    },
    [handleFetchProjects, id, signalTableUpdate],
  )

  const handleQueriesUpdate = React.useCallback(
    ({ limit, offset, search }) => {
      dispatch(
        toUserDepartments({
          pk: user,
          id: id,
          query: { limit, offset, search },
        }),
      )
    },
    [dispatch, user, id, toUserDepartments],
  )

  const handleSetAccess = React.useCallback(
    async ({ selected, projectId }) => {
      const apiMethod = selected ? deleteProjectAccess : postProjectAccess

      const { response } = await apiMethod({
        pk: user,
        departmentId: id,
        projectId,
      })

      // updating react state without refetching
      // data in order to get smooth UX
      if (response) {
        setData({
          ...data,
          results: data.results.map(project => {
            if (projectId === project.id) {
              return {
                ...project,
                members_with_access: selected
                  ? project.members_with_access.filter(
                      member => member.id !== user,
                    )
                  : [...project.members_with_access, selectedUser],
              }
            }

            return project
          }),
        })
      }

      handleFetchAllProjectsAccess({ user, departmentId: id })
    },
    [deleteProjectAccess, postProjectAccess, id, user, data, setData],
  )

  const {
    state: {
      pagination: { currentPage, pageCount, perPage },
      table: { headers, model, isFetching },
    },
    handlers: {
      pagination: { handlePage, handlePageSize },
    },
  } = useTable({
    columns,
    data: data.results,
    mapSortingPropToQueryParam,
    pageCount: data.count,
    defaultPageSize: 7,
    search,
    fetchAction: handleRefetch,
    updateQueriesAction: handleQueriesUpdate,
  })

  return {
    state: {
      pagination: { currentPage, pageCount, perPage },
      table: { headers, model, isFetching, isAllProjectsAccess },
    },
    handlers: {
      pagination: { handlePage, handlePageSize },
      handleSetAccess,
      toggleSelectAll,
    },
  }
}
